import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone, Share, Zap, Bell, Wifi } from 'lucide-react';

/**
 * PWA Install Prompt Component
 * Shows a beautiful modal to encourage users to install the app
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user dismissed the prompt recently (within 7 days)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show the prompt after a delay if not installed
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('DOCKHTA has been installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if already installed or no prompt available (and not iOS)
  if (isStandalone || (!showPrompt)) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-300"
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-4 bottom-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:max-w-sm z-50 animate-in slide-in-from-bottom-5 duration-500">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
          {/* Close button */}
          <button 
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors z-10"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>

          {/* Header with gradient and logo */}
          <div className="relative bg-gradient-to-br from-[#E53935] via-[#D32F2F] to-[#B71C1C] px-6 pt-8 pb-12">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full" />
            </div>
            
            <div className="relative flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                <img src="/favicon-192.png" alt="DOCKHTA" className="w-12 h-12" />
              </div>
              <div className="text-white">
                <h3 className="font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>DOCKHTA</h3>
                <p className="text-white/80 text-sm">Votre santé, notre priorité</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5 -mt-6">
            <div className="bg-white rounded-xl shadow-md border border-slate-100 p-4 mb-4">
              {isIOS ? (
                // iOS specific instructions
                <div className="space-y-4">
                  <p className="text-sm font-medium text-slate-900 text-center">
                    Installez DOCKHTA en 2 étapes :
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center flex-shrink-0">
                        <Share className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">1. Appuyez sur Partager</p>
                        <p className="text-xs text-slate-500">En bas de votre navigateur Safari</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-[#34C759] flex items-center justify-center flex-shrink-0">
                        <Download className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">2. "Sur l'écran d'accueil"</p>
                        <p className="text-xs text-slate-500">Puis confirmez l'ajout</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleDismiss}
                    className="w-full h-11 bg-[#E53935] hover:bg-[#D32F2F] rounded-xl"
                  >
                    J'ai compris
                  </Button>
                </div>
              ) : (
                // Android/Desktop install button
                <div className="space-y-4">
                  <p className="text-sm font-medium text-slate-900 text-center">
                    Profitez d'une meilleure expérience
                  </p>
                  
                  {/* Benefits */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-[#E53935]/10 flex items-center justify-center mx-auto mb-2">
                        <Zap className="w-4 h-4 text-[#E53935]" />
                      </div>
                      <p className="text-xs text-slate-600">Accès rapide</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-[#E53935]/10 flex items-center justify-center mx-auto mb-2">
                        <Bell className="w-4 h-4 text-[#E53935]" />
                      </div>
                      <p className="text-xs text-slate-600">Notifications</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-[#E53935]/10 flex items-center justify-center mx-auto mb-2">
                        <Wifi className="w-4 h-4 text-[#E53935]" />
                      </div>
                      <p className="text-xs text-slate-600">Hors ligne</p>
                    </div>
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleDismiss}
                      className="flex-1 h-11 rounded-xl"
                    >
                      Plus tard
                    </Button>
                    <Button 
                      onClick={handleInstallClick}
                      className="flex-1 h-11 bg-[#E53935] hover:bg-[#D32F2F] rounded-xl font-medium"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Installer
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <p className="text-center text-xs text-slate-400">
              Gratuit • Aucun téléchargement lourd • Sécurisé
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PWAInstallPrompt;
